import React from "react";
import { Flex, Text, Select, TextField, Badge, Tooltip, IconButton, Button } from "@radix-ui/themes";
import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";

// ── Filter field configuration ───────────────────────────────────────────────
// Shared by SegmentsTab (audience definitions) and MarketingAutomations
// (contact-eligibility filters). Keep in sync with the backend allow-list in
// b_marketing/serializers.py (CONTACT_FILTER_ALLOWED_FIELDS).

export const FILTER_FIELDS = [
  { value: "gender",          label: "Gender",          type: "select",  options: [
    { value: "male", label: "Male" }, { value: "female", label: "Female" },
    { value: "other", label: "Other" }, { value: "no_response", label: "No response" },
  ]},
  { value: "age_group",       label: "Age Group",       type: "select",  options: [
    { value: "under_18", label: "Under 18" }, { value: "18_24", label: "18-24" },
    { value: "25_34", label: "25-34" }, { value: "35_44", label: "35-44" },
    { value: "45_54", label: "45-54" }, { value: "55_64", label: "55-64" },
    { value: "65_plus", label: "65+" }, { value: "no_response", label: "No response" },
  ]},
  { value: "email_marketing", label: "Email Consent",   type: "boolean" },
  { value: "sms_marketing",   label: "SMS Consent",     type: "boolean" },
  { value: "whatsapp_marketing", label: "WhatsApp Consent", type: "boolean" },
  { value: "source",          label: "Source",           type: "select",  options: [
    { value: "guest_portal", label: "Guest Portal" }, { value: "sevenrooms", label: "SevenRooms" },
    { value: "eat_app", label: "Eat App" },
    { value: "opentable", label: "OpenTable" }, { value: "mailchimp", label: "Mailchimp" },
    { value: "wix", label: "Wix" }, { value: "manual", label: "Manual" },
  ]},
  { value: "email_validation_status", label: "Email validation status", type: "select", options: [
    { value: "pending", label: "Pending" }, { value: "processing", label: "Processing" },
    { value: "valid", label: "Valid" }, { value: "risky", label: "Risky" },
    { value: "unknown", label: "Unknown" }, { value: "invalid", label: "Invalid" },
  ]},
  // Kept only so existing persisted rules remain readable/editable. New rules
  // must use the canonical field above.
  { value: "email_validity",  label: "Email Validity (legacy)", legacy: true, type: "select",  options: [
    { value: "unchecked", label: "Unchecked" }, { value: "valid", label: "Valid" },
    { value: "invalid", label: "Invalid" },
  ]},
  { value: "locations",       label: "Location",        type: "location" },
  { value: "tags",            label: "Tag",             type: "tag" },
  { value: "created_at",      label: "Date Added",      type: "date" },
  { value: "visit_count",     label: "Visit Count",     type: "number" },
  { value: "first_name",      label: "First Name",      type: "text" },
  { value: "last_name",       label: "Last Name",       type: "text" },
  { value: "email_address",   label: "Email Address",   type: "text" },
  { value: "phone_number",    label: "Phone Number",    type: "text" },
];

export const OPERATORS_BY_TYPE = {
  text:     [
    { value: "equals", label: "equals" }, { value: "not_equals", label: "does not equal" },
    { value: "contains", label: "contains" }, { value: "not_contains", label: "does not contain" },
  ],
  select:   [
    { value: "equals", label: "is" }, { value: "not_equals", label: "is not" },
    { value: "in", label: "is any of" }, { value: "not_in", label: "is none of" },
  ],
  boolean:  [
    { value: "is_true", label: "is opted-in" }, { value: "is_false", label: "is not opted-in" },
  ],
  date:     [
    { value: "before", label: "is before" }, { value: "after", label: "is after" },
    { value: "between", label: "is between" },
  ],
  number:   [
    { value: "equals", label: "equals" }, { value: "not_equals", label: "does not equal" },
    { value: "greater_than", label: "is greater than" }, { value: "less_than", label: "is less than" },
    { value: "between", label: "is between" },
  ],
  location: [
    { value: "in", label: "includes" }, { value: "not_in", label: "excludes" },
  ],
  tag: [
    { value: "in", label: "includes" }, { value: "not_in", label: "excludes" },
  ],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fieldConfig(fieldValue) {
  return FILTER_FIELDS.find((f) => f.value === fieldValue);
}

export function operatorsForField(fieldValue) {
  const cfg = fieldConfig(fieldValue);
  return cfg ? (OPERATORS_BY_TYPE[cfg.type] || []) : [];
}

export function needsValue(operator) {
  return !["is_true", "is_false"].includes(operator);
}

export function emptyRule() {
  return { field: "", operator: "", value: "" };
}

export function rulesAreValid(rules) {
  if (!rules || rules.length === 0) return false;
  return rules.every((r) => {
    if (!r.field || !r.operator) return false;
    if (needsValue(r.operator)) {
      if (Array.isArray(r.value)) return r.value.length > 0;
      return r.value !== "" && r.value !== undefined && r.value !== null;
    }
    return true;
  });
}

// ══════════════════════════════════════════════════════════════════════════════
//  Rule Row
// ══════════════════════════════════════════════════════════════════════════════

export function RuleRow({ rule, index, locations, tags, onChange, onRemove, matchMode, totalRules }) {
  const cfg = fieldConfig(rule.field);
  const operators = operatorsForField(rule.field);
  const showValue = needsValue(rule.operator);
  const selectableFields = FILTER_FIELDS.filter((field) => !field.legacy || field.value === rule.field);

  const handleFieldChange = (val) => {
    const newOps = operatorsForField(val);
    onChange(index, { field: val, operator: newOps[0]?.value || "", value: "" });
  };

  const handleOperatorChange = (val) => {
    // Reset value when switching between single/multi operators
    const prevIsMulti = ["in", "not_in"].includes(rule.operator);
    const nextIsMulti = ["in", "not_in"].includes(val);
    const newValue = prevIsMulti !== nextIsMulti ? "" : rule.value;
    onChange(index, { ...rule, operator: val, value: newValue });
  };

  const handleValueChange = (val) => {
    onChange(index, { ...rule, value: val });
  };

  // Render value input based on field type + operator
  const renderValueInput = () => {
    if (!showValue) return null;

    // Multi-select (in / not_in) for select fields
    if (cfg?.type === "select" && ["in", "not_in"].includes(rule.operator)) {
      const selected = Array.isArray(rule.value) ? rule.value : [];
      return (
        <Flex gap="1" wrap="wrap" align="center" style={{ flex: 1, minWidth: 160 }}>
          {cfg.options.map((opt) => {
            const active = selected.includes(opt.value);
            return (
              <Badge
                key={opt.value}
                variant={active ? "solid" : "outline"}
                color={active ? "blue" : "gray"}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  const next = active
                    ? selected.filter((v) => v !== opt.value)
                    : [...selected, opt.value];
                  handleValueChange(next);
                }}
              >
                {opt.label}
              </Badge>
            );
          })}
        </Flex>
      );
    }

    // Single select
    if (cfg?.type === "select") {
      return (
        <Select.Root value={rule.value || ""} onValueChange={handleValueChange}>
          <Select.Trigger placeholder="Select value…" style={{ minWidth: 140 }} />
          <Select.Content>
            {cfg.options.map((opt) => (
              <Select.Item key={opt.value} value={opt.value}>{opt.label}</Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      );
    }

    // Location multi-select
    if (cfg?.type === "location") {
      const selected = Array.isArray(rule.value) ? rule.value : [];
      return (
        <Flex gap="1" wrap="wrap" align="center" style={{ flex: 1, minWidth: 160 }}>
          {(locations || []).map((loc) => {
            const active = selected.includes(loc.id);
            return (
              <Badge
                key={loc.id}
                variant={active ? "solid" : "outline"}
                color={active ? "blue" : "gray"}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  const next = active
                    ? selected.filter((v) => v !== loc.id)
                    : [...selected, loc.id];
                  handleValueChange(next);
                }}
              >
                {loc.name}
              </Badge>
            );
          })}
          {(locations || []).length === 0 && <Text size="1" color="gray">No locations available</Text>}
        </Flex>
      );
    }

    // Tag multi-select
    if (cfg?.type === "tag") {
      const selected = Array.isArray(rule.value) ? rule.value : [];
      return (
        <Flex gap="1" wrap="wrap" align="center" style={{ flex: 1, minWidth: 160 }}>
          {(tags || []).map((tag) => {
            const active = selected.includes(tag.tag_id);
            return (
              <Badge
                key={tag.tag_id}
                variant={active ? "solid" : "outline"}
                color={active ? "blue" : "gray"}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  const next = active
                    ? selected.filter((v) => v !== tag.tag_id)
                    : [...selected, tag.tag_id];
                  handleValueChange(next);
                }}
              >
                {tag.name}
              </Badge>
            );
          })}
          {(tags || []).length === 0 && <Text size="1" color="gray">No tags available</Text>}
        </Flex>
      );
    }

    // Date fields
    if (cfg?.type === "date") {
      if (rule.operator === "between") {
        const vals = Array.isArray(rule.value) ? rule.value : ["", ""];
        return (
          <Flex gap="2" align="center" style={{ flex: 1 }}>
            <TextField.Root type="date" value={vals[0] || ""}
              onChange={(e) => handleValueChange([e.target.value, vals[1]])} style={{ flex: 1 }} />
            <Text size="1" color="gray">and</Text>
            <TextField.Root type="date" value={vals[1] || ""}
              onChange={(e) => handleValueChange([vals[0], e.target.value])} style={{ flex: 1 }} />
          </Flex>
        );
      }
      return (
        <TextField.Root type="date" value={rule.value || ""}
          onChange={(e) => handleValueChange(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
      );
    }

    // Number (including between)
    if (cfg?.type === "number") {
      if (rule.operator === "between") {
        const vals = Array.isArray(rule.value) ? rule.value : ["", ""];
        return (
          <Flex gap="2" align="center" style={{ flex: 1 }}>
            <TextField.Root type="number" placeholder="Min" value={vals[0] ?? ""}
              onChange={(e) => handleValueChange([e.target.value, vals[1]])} style={{ flex: 1 }} />
            <Text size="1" color="gray">and</Text>
            <TextField.Root type="number" placeholder="Max" value={vals[1] ?? ""}
              onChange={(e) => handleValueChange([vals[0], e.target.value])} style={{ flex: 1 }} />
          </Flex>
        );
      }
      return (
        <TextField.Root type="number" placeholder="Value…" value={rule.value ?? ""}
          onChange={(e) => handleValueChange(e.target.value)} style={{ flex: 1, minWidth: 100 }} />
      );
    }

    // Default text
    return (
      <TextField.Root placeholder="Value…" value={rule.value ?? ""}
        onChange={(e) => handleValueChange(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
    );
  };

  return (
    <Flex gap="2" align="start" style={{ alignSelf: "stretch" }} wrap="wrap">
      {/* Row number + match label */}
      <Flex align="center" gap="2" style={{ minWidth: 60, paddingTop: "var(--space-2)" }}>
        {index === 0 ? (
          <Text size="1" color="gray" weight="medium">Where</Text>
        ) : (
          <Badge variant="soft" color={matchMode === "all" ? "blue" : "orange"} size="1">
            {matchMode === "all" ? "AND" : "OR"}
          </Badge>
        )}
      </Flex>

      {/* Field selector */}
      <Select.Root value={rule.field || ""} onValueChange={handleFieldChange}>
        <Select.Trigger placeholder="Select field…" style={{ minWidth: 140 }} />
        <Select.Content>
          {selectableFields.map((f) => (
            <Select.Item key={f.value} value={f.value}>{f.label}</Select.Item>
          ))}
        </Select.Content>
      </Select.Root>

      {/* Operator selector */}
      {rule.field && (
        <Select.Root value={rule.operator || ""} onValueChange={handleOperatorChange}>
          <Select.Trigger placeholder="operator" style={{ minWidth: 140 }} />
          <Select.Content>
            {operators.map((op) => (
              <Select.Item key={op.value} value={op.value}>{op.label}</Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      )}

      {/* Value */}
      {rule.field && rule.operator && renderValueInput()}

      {/* Remove button */}
      {totalRules > 1 && (
        <Tooltip content="Remove rule">
          <IconButton variant="ghost" color="red" size="1" onClick={() => onRemove(index)}
            style={{ marginTop: "var(--space-2)" }}>
            <Cross2Icon />
          </IconButton>
        </Tooltip>
      )}
    </Flex>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Contact Filter Builder (match mode + rules + add)
// ══════════════════════════════════════════════════════════════════════════════

export default function ContactFilterBuilder({
  matchMode,
  onMatchModeChange,
  rules,
  onRulesChange,
  locations = [],
  tags = [],
}) {
  const handleRuleChange = (idx, updated) => {
    onRulesChange(rules.map((r, i) => (i === idx ? updated : r)));
  };
  const handleRuleRemove = (idx) => {
    onRulesChange(rules.filter((_, i) => i !== idx));
  };
  const addRule = () => onRulesChange([...rules, emptyRule()]);

  return (
    <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
      {/* Match mode */}
      <Flex direction="column" gap="1">
        <Text size="2" weight="medium">Match contacts where</Text>
        <Select.Root value={matchMode} onValueChange={onMatchModeChange}>
          <Select.Trigger style={{ maxWidth: 260 }} />
          <Select.Content>
            <Select.Item value="all">All rules match (AND)</Select.Item>
            <Select.Item value="any">Any rule matches (OR)</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>

      {/* Rules */}
      <Flex direction="column" gap="3"
        style={{ background: "var(--gray-a2)", borderRadius: "var(--radius-3)", padding: "var(--space-3)" }}>
        {rules.map((rule, idx) => (
          <RuleRow
            key={idx}
            rule={rule}
            index={idx}
            locations={locations}
            tags={tags}
            onChange={handleRuleChange}
            onRemove={handleRuleRemove}
            matchMode={matchMode}
            totalRules={rules.length}
          />
        ))}
        <Button variant="soft" color="gray" size="1" onClick={addRule} style={{ alignSelf: "flex-start" }}>
          <PlusIcon /> Add rule
        </Button>
      </Flex>
    </Flex>
  );
}
